/* eslint-disable no-useless-return */
import { Auth } from 'aws-amplify';
import * as types from './action-types';
import * as cognitoChallengeNames from './cognito-challenge-names';
import * as signInFormApi from '../api/sign-in-form-api';
import { beginApiCall, apiCallError } from './api-status-actions';
import { formConstants } from '../../components/utils/constants/form-constants';
import userService from '../../../../general/js/user/user-service';
import * as cookieHelper from '../../helpers/cookie-helper';
import * as apiResponseMessages from './api-response-messages';
import * as dataFormatHelper from './helpers/data-format-helper';
import { isEmpty } from '../../components/utils/obj-utils';
import { formMessageConstants } from '../../components/utils/constants/form-message-constants';
import { LAST_VISIT_DATE } from '../../../../general/js/user/constants';
import * as messageHelper from './helpers/message-helper';
import { updateUserDataCookie } from '../../components/utils/user-data-cookie/user-data-cookie-helpers';
import { reloadToBasePage } from '../../../../general/js/utils';

export const PASSWORD_RESET_REQUIRED_EXCEPTION = 'PasswordResetRequiredException';
const USER_LAMBDA_VALIDATION_EXCEPTION = 'UserLambdaValidationException';
const USER_NOT_FOUND_EXCEPTION = 'UserNotFoundException';

export function loadSSOSignInFormDataSuccess(signInFormData) {
    return { type: types.LOAD_SIGN_IN_FORM_DATA_SUCCESS, signInFormData };
}

export function loadSSOSignInFormDataError() {
    return { type: types.LOAD_SIGN_IN_FORM_DATA_ERROR };
}

export function loadSSOSignInSuccessFormDataSuccess(signInFormDataSuccess) {
    return { type: types.LOAD_SIGN_IN_SUCCESS_FORM_DATA_SUCCESS, signInFormDataSuccess };
}

export function loadNewPasswordRequiredFormDataSuccess(newPasswordRequiredFormData) {
    return { type: types.LOAD_NEW_PASSWORD_REQUIRED_FORM_DATA_SUCCESS, newPasswordRequiredFormData };
}

export function loadNewPasswordRequiredFormDataError(loadPasswordRequiredFormDataMessages) {
    return { type: types.LOAD_NEW_PASSWORD_REQUIRED_FORM_DATA_ERROR, loadPasswordRequiredFormDataMessages };
}

export function signInUserSuccess(signInProperties) {
    return { type: types.SIGN_IN_USER_SUCCESS, signInProperties };
}

export function signInUserError(signInMessages) {
    return { type: types.SIGN_IN_USER_ERROR, signInMessages };
}

export function lastLogInDataUpdateSuccess(lastLogInProperties) {
    return { type: types.LAST_LOG_IN_UPDATE_SUCCESS, lastLogInProperties };
}

export function lastLogInDataUpdateError(errorMessages) {
    return { type: types.LAST_LOG_IN_UPDATE_ERROR, errorMessages };
}

export function newPasswordSavedSuccess(newPasswordSavedMessages) {
    return { type: types.NEW_PASSWORD_SAVED_SUCCESS, newPasswordSavedMessages };
}

export function newPasswordSavedError(newPasswordSavedMessages) {
    return { type: types.NEW_PASSWORD_SAVED_ERROR, newPasswordSavedMessages };
}

export function signOutUserError(signOutMessages) {
    return { type: types.SIGN_OUT_USER_SUCCESS, signOutMessages };
}

export function signOutUserSuccess(signOutMessages) {
    return { type: types.SIGN_OUT_USER_ERROR, signOutMessages };
}

export function resetSignInFormState() {
    return { type: types.RESET_SIGN_IN_FORM_STATE };
}

export function loadSSOSignInFormData() {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi.getSSOSignInFormData()
            .then((response) => {
                if (response.hasErrors) {
                    dispatch(apiCallError([response.errorMessage]));
                    dispatch(loadSSOSignInFormDataError());
                    return;
                }
                dispatch(loadSSOSignInFormDataSuccess(response.data));
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(loadSSOSignInFormDataError());
                throw error;
            });
    };
}

export function loadSSOSignInSuccessFormData() {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi.getSSOSignInSuccessFormData()
            .then((response) => {
                if (response.hasErrors) {
                    dispatch(apiCallError([response.errorMessage]));
                    dispatch(loadSSOSignInFormDataError());
                    return;
                }
                dispatch(loadSSOSignInSuccessFormDataSuccess(response.data));
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(loadSSOSignInFormDataError());
                throw error;
            });
    };
}

export function loadNewPasswordRequiredFormData() {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi.getNewPasswordRequiredFormData()
            .then((response) => {
                if (isEmpty(response)) {
                    dispatch(apiCallError([formMessageConstants.somethingWentWrong]));
                    dispatch(loadNewPasswordRequiredFormDataError([formMessageConstants.somethingWentWrong]));
                    return;
                }

                if (response.hasErrors) {
                    dispatch(apiCallError([response.errorMessage]));
                    dispatch(loadNewPasswordRequiredFormDataError([response.errorMessage]));
                    return;
                }

                dispatch(loadNewPasswordRequiredFormDataSuccess(response.data));
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(loadNewPasswordRequiredFormDataError());
                throw error;
            });
    };
}

export function handleNewPasswordRequired({
    cognitoUser,
    password,
    maxIdleTimeInSeconds,
    actionSuccessText
}) {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi
            .completeNewPassword({ cognitoUser, password })
            .then((response) => {
                if (response.hasErrors) {
                    dispatch(apiCallError([response.errorMessage]));
                    dispatch(newPasswordSavedError([apiResponseMessages.SERVER_ERROR_MESSAGE]));
                    return;
                }
                finalizeNewPasswordChange(dispatch, { maxIdleTimeInSeconds, actionSuccessText });
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(newPasswordSavedError([apiResponseMessages.SERVER_ERROR_MESSAGE]));
                throw error;
            });
    };
}

function finalizeNewPasswordChange(dispatch, { maxIdleTimeInSeconds, actionSuccessText }) {
    Auth.currentSession()
        .then((data) => {
            const { jwtToken } = data.accessToken;
            userService.setAccessTokenCookie(jwtToken);
            cookieHelper.setCookie(formConstants.maxIdleTimeInSeconds, maxIdleTimeInSeconds);
            dispatch(newPasswordSavedSuccess([actionSuccessText]));
        }).catch((error) => {
            dispatch(apiCallError([error]));
            dispatch(newPasswordSavedError([apiResponseMessages.SERVER_ERROR_MESSAGE]));
        });
}

export function updateUserSignInDetails({
    lastLoginDate,
    lastLoginDevice,
    lastLoginPlatform,
    callBackUrl
}) {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi.updateUserSignInDetails(
            dataFormatHelper.getFormattedUserSignInDetailsData(lastLoginDate, lastLoginDevice, lastLoginPlatform),
            callBackUrl
        )
            .then((response) => {
                if (response.hasErrors) {
                    const serverErrorMessages = messageHelper.getServerErrorMessages(response);
                    dispatch(apiCallError(serverErrorMessages));
                    dispatch(lastLogInDataUpdateError(serverErrorMessages));
                    return;
                }

                userService.setDataInLocalStorage(LAST_VISIT_DATE, lastLoginDate);

                dispatch(lastLogInDataUpdateSuccess({
                    isUserLastLogInDataUpdated: true,
                }));
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(lastLogInDataUpdateError([error]));
                throw error;
            });
    };
}

export function signInUser(userSignInData) {
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi
            .signInUser(dataFormatHelper.getFormattedUserSignInData(userSignInData))
            .then((response) => {
                const { challengeName } = response;
                const { actionSuccessText } = userSignInData;
                if (challengeName === cognitoChallengeNames.NEW_PASSWORD_REQUIRED) {
                    dispatch(signInUserSuccess({
                        isSignInSuccessful: true,
                        isNewPasswordRequired: true,
                        signInMessages: [actionSuccessText],
                        cognitoUser: response
                    }));
                    return;
                }

                finalizeUserSignIn(dispatch, userSignInData, response);
            })
            .catch((error) => {
                let errorMessages = isEmpty(error.message) ? [apiResponseMessages.SERVER_ERROR_MESSAGE] : [error.message];
                if (error.code === PASSWORD_RESET_REQUIRED_EXCEPTION) {
                    errorMessages = [apiResponseMessages.PASSWORD_RESET_REQUIRED];
                }

                if (error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
                    errorMessages = [apiResponseMessages.MUST_CONFIRM_EMAIL_MESSAGE];
                }

                if (error.code === USER_NOT_FOUND_EXCEPTION) {
                    errorMessages = [apiResponseMessages.MISSING_USER];
                }

                dispatch(apiCallError([error]));
                dispatch(signInUserError(errorMessages));
                throw error;
            });
    };
}

function finalizeUserSignIn(dispatch, { maxIdleTimeInSeconds, actionSuccessText }, response) {
    Auth.currentSession()
        .then((data) => {
            const { jwtToken } = data.accessToken;
            userService.setAccessTokenCookie(jwtToken);
            cookieHelper.setCookie(formConstants.maxIdleTimeInSeconds, maxIdleTimeInSeconds);
            updateUserDataCookie();
            dispatch(signInUserSuccess({
                isSignInSuccessful: true,
                isNewPasswordRequired: false,
                signInMessages: [actionSuccessText],
                cognitoUser: response
            }));
        }).catch((error) => {
            dispatch(apiCallError([error]));
            dispatch(signInUserError([apiResponseMessages.SERVER_ERROR_MESSAGE]));
        });
}

export function signOutUser() {
    //eslint-disable-next-line no-unused-vars
    return function (dispatch) {
        dispatch(beginApiCall());
        return signInFormApi
            .signOutUser()
            .then(() => {
                dispatch(signOutUserSuccess([apiResponseMessages.SIGN_OUT_USER_SUCCESSFULLY]));
                userService.setAccessTokenCookie(null);
                userService.setUserDataCookie(null);
                cookieHelper.setCookie(formConstants.maxIdleTimeInSeconds, 0);
                reloadToBasePage();
            })
            .catch((error) => {
                dispatch(apiCallError([error]));
                dispatch(signOutUserError([apiResponseMessages.SERVER_ERROR_MESSAGE]));
                throw error;
            });
    };
}
